import { inject, Injectable } from '@angular/core';
import { PaceBarcode } from '@evorait/pace-components';
import { Actions, ofType } from '@ngrx/effects';
import { Action, Store } from '@ngrx/store';
import { BsModalService } from 'ngx-bootstrap/modal';
import { Observable, of } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { ConfigConstants } from '../../config/config-constants';
import { BarcodeScannerInterface } from '../../interfaces/barcode-scanner.interface';
import {
    barcodeClipboardPersisted,
    barcodeClipboardStorageError,
    hydrateBarcodeClipboard,
    setBarcodeClipboardRequested,
} from '../../state/barcode-clipboard/actions/barcode-clipboard.actions';
import { selectBarcodeClipboard } from '../../state/barcode-clipboard/selectors/barcode-clipboard.selector';
import { BarcodeScanResult } from './barcode-scanner.const';
import { openBarcodeScannerModal } from './barcode-scanner.modal';

/**
 * NgRx-backed implementation of {@link BarcodeScannerInterface}. The barcode clipboard lives in
 * the barcode-clipboard store slice, persisted through `BarcodeClipboardEffects`; every write
 * dispatches a request action and awaits its correlated settle action. `scan()` stays pure UI
 * orchestration - it opens the scanner modal via the shared {@link openBarcodeScannerModal}
 * helper and hands back its result observable directly, since there is nothing to route through
 * the store.
 */
@Injectable({ providedIn: 'root' })
export class BarcodeScannerNgrxService implements BarcodeScannerInterface {
    private readonly store = inject(Store);
    private readonly actions$ = inject(Actions);
    private readonly modalService = inject(BsModalService);

    /** Monotonic counter used to correlate a write request with its settle action. */
    private correlationCounter = 0;

    // The slice stores the clipboard as `readonly PaceBarcode[]` (the reducer's cap is the only
    // mutation point) while the interface declares a mutable array, so the cast is the boundary
    // translation - callers never mutate the emitted array in practice.
    public readonly barcodeClipboard$: Observable<PaceBarcode[]> = this.store.select(
        selectBarcodeClipboard,
    ) as Observable<PaceBarcode[]>;

    public constructor() {
        this.store.dispatch(hydrateBarcodeClipboard());
    }

    public scan(
        pattern?: string,
        appId = ConfigConstants.ApplicationIdentifier.Default,
        clipboardAccessible = true,
    ): Observable<BarcodeScanResult> {
        return openBarcodeScannerModal(this.modalService, { pattern, appId, clipboardAccessible });
    }

    public setBarcodeClipboard(barcodes: PaceBarcode[]): Observable<boolean> {
        const correlationId = `barcode-clipboard-set-${++this.correlationCounter}`;
        return this.dispatchAndAwait(setBarcodeClipboardRequested({ barcodes, correlationId }), correlationId);
    }

    public uploadBarcodeClipboard(): Observable<boolean> {
        // irrelevant here
        return of(true);
    }

    public fetchBarcodeClipboard(): Observable<boolean> {
        // irrelevant here
        return of(true);
    }

    /**
     * Dispatches a write request and awaits the settle action carrying the same correlation id, so
     * concurrent writes cannot settle each other. The subscription is set up before dispatching,
     * because an effect that answers synchronously would otherwise be missed.
     *
     * @param request the write-request action to dispatch, carrying the correlation id
     * @param correlationId the id carried by the request and echoed by the settle action
     * @returns an observable emitting `true` on `barcodeClipboardPersisted`, `false` on
     * `barcodeClipboardStorageError`
     */
    private dispatchAndAwait(request: Action, correlationId: string): Observable<boolean> {
        return new Observable<boolean>((subscriber) => {
            const subscription = this.actions$
                .pipe(
                    ofType(barcodeClipboardPersisted, barcodeClipboardStorageError),
                    filter((action) => action.correlationId === correlationId),
                    take(1),
                )
                .subscribe((action) => {
                    subscriber.next(action.type === barcodeClipboardPersisted.type);
                    subscriber.complete();
                });
            this.store.dispatch(request);
            return () => subscription.unsubscribe();
        });
    }
}
